import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import useEmblaCarousel from "embla-carousel-react";
import {
  Check,
  X,
  Plus,
  Star,
  Compass,
  ArrowRight,
} from "lucide-react";
import PackageQuiz from "@/components/landing/PackageQuiz";
import StudioReelVideo from "@/components/landing/StudioReelVideo";
import AlaCarteModal from "@/components/landing/AlaCarteModal";
import { PackageCardMobile } from "@/components/landing/PackageCardMobile";
import { PackageCardDesktop } from "@/components/landing/PackageCardDesktop";
import { FloatingOrbs, CornerHeadphones } from "@/components/landing/AmbientMotion";
import { AmbientDust, ScrubLine, SpinningRecord, RecordingBadge, FrequencyTicker, HugeWatermarkText } from "@/components/landing/AmbientMotion";
import { ADD_ONS, AUDIENCES } from "@/data/packagesData";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.07, duration: 0.7, ease: [0.16, 1, 0.3, 1] },
  }),
};

const Packages = () => {
  // slideIndex stays in {0, 1, 2}. The middle (1) is the visible "current" panel.
  // After settling on 0 or 2, we update currentAudience and silently snap back to 1.
  const [slideIndex, setSlideIndex] = useState(1);
  const [enableAnim, setEnableAnim] = useState(true);
  const [audience, setAudience] = useState("creators");
  const N = AUDIENCES.length;
  const modIdx = AUDIENCES.findIndex((a) => a.id === audience);
  const current = AUDIENCES[modIdx];
  const [quizOpen, setQuizOpen] = useState(false);
  const [alaCarteOpen, setAlaCarteOpen] = useState(false);
  const carouselRef = useRef(null);
  const tierScrollRef = useRef(null);
  const [activeTier, setActiveTier] = useState(0);
  const wheelCooldownRef = useRef(0);
  const wheelAccumRef = useRef(0);

  // Detect mobile so we can (a) disable the outer audience-drag to prevent
  // gesture conflict with the inner tier snap-scroll, and (b) render the
  // tier list as a horizontal carousel instead of a vertical stack.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Track which mobile tier card is centered so the dots indicator updates.
  useEffect(() => {
    if (!isMobile) return;
    const el = tierScrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const cards = el.querySelectorAll("[data-tier-card]");
      const center = el.scrollLeft + el.clientWidth / 2;
      let best = 0;
      let bestDist = Infinity;
      cards.forEach((c, i) => {
        const cCenter = c.offsetLeft + c.offsetWidth / 2;
        const d = Math.abs(cCenter - center);
        if (d < bestDist) { bestDist = d; best = i; }
      });
      setActiveTier(best);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [isMobile, audience]);

  const scrollTierTo = (i) => {
    const el = tierScrollRef.current;
    if (!el) return;
    const card = el.querySelectorAll("[data-tier-card]")[i];
    if (!card) return;
    el.scrollTo({
      left: card.offsetLeft - (el.clientWidth - card.offsetWidth) / 2,
      behavior: "smooth",
    });
  };

  // ─── Mobile infinite-loop carousel across ALL audiences ────────────────────
  // Flatten every tier from every audience into a single list. Card 0 → 2 =
  // creators, cards 3 → 5 = brands. The audience pill auto-updates as the user
  // scrolls; loop=true means scrolling past either end wraps around.
  const flatTiers = useMemo(
    () =>
      AUDIENCES.flatMap((a) =>
        a.tiers.map((t) => ({ ...t, audienceId: a.id, audienceLabel: a.label })),
      ),
    [],
  );
  const [flatIndex, setFlatIndex] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel(
    // No WheelGesturesPlugin — vertical mousewheel/trackpad must pass through
    // to the page. Horizontal drag/swipe still moves the carousel via Embla's
    // built-in drag handler, which locks direction only after a horizontal
    // move exceeds threshold — so vertical touch keeps scrolling the page.
    { loop: true, align: "center", containScroll: false, dragFree: false, axis: "x" },
  );

  // When the centered slide changes, sync `audience` and `flatIndex`.
  useEffect(() => {
    if (!isMobile || !emblaApi) return;
    const onSelect = () => {
      const i = emblaApi.selectedScrollSnap();
      setFlatIndex(i);
      const nextAudience = flatTiers[i]?.audienceId;
      if (nextAudience) setAudience(nextAudience);
    };
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [isMobile, emblaApi, flatTiers]);

  // When the user taps a pill, jump the carousel to that audience's first tier.
  const jumpToAudience = useCallback(
    (audId) => {
      setAudience(audId);
      if (!isMobile || !emblaApi) return;
      const first = flatTiers.findIndex((t) => t.audienceId === audId);
      if (first >= 0) emblaApi.scrollTo(first);
    },
    [isMobile, emblaApi, flatTiers],
  );

  const emblaPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const emblaNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  // Helpers
  const audienceAt = (offset) => {
    const idx = ((modIdx + offset) % N + N) % N;
    return AUDIENCES[idx];
  };
  // Panels rendered: [prev, current, next] for the sliding window
  const panelOrder = [audienceAt(-1), current, audienceAt(1)];

  const slideForward = () => {
    if (slideIndex !== 1) return;
    setSlideIndex(2);
  };
  const slideBackward = () => {
    if (slideIndex !== 1) return;
    setSlideIndex(0);
  };

  const switchAudience = (next) => {
    if (next === audience) return;
    const nextIdx = AUDIENCES.findIndex((a) => a.id === next);
    let delta = nextIdx - modIdx;
    if (delta > N / 2) delta -= N;
    if (delta < -N / 2) delta += N;
    if (delta > 0) slideForward();
    else if (delta < 0) slideBackward();
  };

  const handleSwipe = (offsetX, velocityX) => {
    if (isMobile) return; // Mobile uses inner tier snap-scroll; no outer audience swipe.
    const swipe = offsetX + velocityX * 0.25;
    if (swipe < -120) slideForward();
    else if (swipe > 120) slideBackward();
  };

  // After the slide lands on 0 or 2, update the audience and silently snap back to 1.
  const onSlideComplete = () => {
    if (slideIndex === 1) return;
    const newAudience = audienceAt(slideIndex === 2 ? 1 : -1).id;
    setEnableAnim(false);
    setAudience(newAudience);
    setSlideIndex(1);
    requestAnimationFrame(() => requestAnimationFrame(() => setEnableAnim(true)));
  };

  // Trackpad / smart-mouse horizontal scroll → continuous loop slide
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;

    let resetTimer = null;
    const onWheel = (e) => {
      // Only handle when the user is intentionally scrolling horizontally.
      const dx = e.deltaX;
      const dy = e.deltaY;
      if (Math.abs(dx) <= Math.abs(dy) + 4) return; // vertical scroll → let the page scroll

      e.preventDefault();
      const now = performance.now();
      if (now < wheelCooldownRef.current) return; // throttle

      // Accumulate small momentum deltas; commit on a meaningful threshold.
      wheelAccumRef.current += dx;
      if (Math.abs(wheelAccumRef.current) < 60) {
        if (resetTimer) clearTimeout(resetTimer);
        resetTimer = setTimeout(() => {
          wheelAccumRef.current = 0;
        }, 250);
        return;
      }

      const dir = wheelAccumRef.current > 0 ? 1 : -1;
      wheelAccumRef.current = 0;
      if (resetTimer) clearTimeout(resetTimer);

      // Continuous loop — keep stepping forward/backward regardless of edge
      if (dir > 0) slideForward();
      else slideBackward();
      wheelCooldownRef.current = now + 1000;
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheel);
      if (resetTimer) clearTimeout(resetTimer);
    };
  }, []);

  return (
    <section
      id="packages"
      data-testid="packages-section"
      className="relative py-16 md:py-24"
    >
      <FloatingOrbs colors={["#6b21d9", "#00f0ff", "#ff0055"]} />
      <CornerHeadphones color="#00f0ff" corner="tr" size={44} />
      <AmbientDust count={20} color="#00f0ff" />
      <RecordingBadge corner="tl" color="#ff0055" />
      <FrequencyTicker corner="br" color="#22c55e" duration={35} />
      <HugeWatermarkText text="PACKAGES" corner="bl" color="#a78bfa" size={200} />
      <div className="absolute -bottom-16 -left-20 pointer-events-none opacity-30">
        <SpinningRecord size={320} color="#ff0055" speed={70} />
      </div>
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-[20%] left-[40%] w-[600px] h-[600px] rounded-full bg-[#6b21d9]/20 blur-[160px]" />
      </div>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-12 gap-6 items-end mb-10"
        >
          <motion.div variants={reveal} custom={0} className="col-span-12 md:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[#00f0ff] mb-5">
              [ 03 · Packages ]
            </div>
            <h2
              data-testid="packages-heading"
              className="font-display font-black tracking-tighter text-[44px] md:text-[64px] lg:text-[80px] leading-[0.95]"
            >
              Flexible packages.{" "}
              <span className="italic font-light text-[#a395c3]">
                Pick your lane.
              </span>
            </h2>
          </motion.div>
          <motion.p
            variants={reveal}
            custom={1}
            className="col-span-12 md:col-span-5 text-[#a395c3] text-base md:text-[17px] leading-relaxed"
          >
            Two paths to working with SB Media Studio — one tuned for creators
            building an audience, one for brands building a presence. Same
            studio, same standards.
          </motion.p>
        </motion.div>

        {/* Test Run — featured no-risk entry card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="relative rounded-3xl p-7 md:p-9 mb-12 overflow-hidden glow-border bg-[#11091a]/70 backdrop-blur-md"
          data-testid="packages-test-run-card"
        >
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#00f0ff]/20 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-[#a855f7]/20 blur-3xl pointer-events-none" />

          <div className="relative grid grid-cols-12 gap-6 md:gap-10 items-start">
            <div className="col-span-12 lg:col-span-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.28em] font-semibold border border-[#00f0ff]/40 text-[#00f0ff] bg-[#00f0ff]/10">
                <Star size={11} /> Starter · No-risk trial
              </div>
              <div className="mt-5 font-display font-bold text-[38px] md:text-[48px] tracking-tighter text-[#00f0ff] leading-none">
                Test Run
              </div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3] mt-3">
                30-min studio session
              </div>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-display font-black text-5xl md:text-6xl tracking-tighter">
                  $150
                </span>
                <span className="text-[#a395c3] text-sm">/ session</span>
              </div>
              <a
                href="#book"
                onClick={(e) => {
                  e.preventDefault();
                  window.dispatchEvent(
                    new CustomEvent("sb:open-booking", {
                      detail: { package: "test_run" },
                    }),
                  );
                }}
                data-testid="packages-test-run-cta"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-[#00f0ff] text-[#05050a] font-semibold text-sm hover:bg-white transition-colors"
              >
                Book your Test Run
              </a>
              <p className="mt-5 text-[12px] text-[#a395c3] leading-relaxed">
                The lowest-risk way to try the studio. Same gear, shorter
                session — get a feel for the room before stepping up to a full
                package.
              </p>
            </div>

            <div className="col-span-12 lg:col-span-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-[#00f0ff] mb-4">
                    What&apos;s included
                  </div>
                  <ul className="space-y-3 text-[14px] text-white/90">
                    {[
                      "30-minute session",
                      "2 camera angles",
                      "Basic Lighting",
                      "1080p, live production",
                      "Same-day deliverable",
                    ].map((f) => (
                      <li key={f} className="flex items-start gap-2.5">
                        <Check
                          size={14}
                          className="text-[#00f0ff] mt-1 flex-shrink-0"
                          strokeWidth={2.5}
                        />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-[#a855f7] mb-4">
                    Unlock with Studio Day
                  </div>
                  <ul className="space-y-3 text-[14px] text-white/55">
                    {[
                      "Up to 2-hour studio session",
                      "3 Sony 4K camera set-up",
                      "Studio lighting and professional audio (Mics + monitoring)",
                      "48 hr delivery",
                    ].map((f) => (
                      <li key={f} className="flex items-start gap-2.5">
                        <X
                          size={14}
                          className="text-[#a855f7] mt-1 flex-shrink-0"
                          strokeWidth={2.5}
                        />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Full Production By The Hour — for true beginners */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="rounded-3xl p-7 md:p-9 mb-14 relative overflow-hidden bg-[#11091a]/70 backdrop-blur-md border border-white/10"
          data-testid="packages-full-production-hourly"
        >
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#ff0055]/12 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-[#00f0ff]/10 blur-3xl pointer-events-none" />
          <div className="relative grid grid-cols-12 gap-6 md:gap-10 items-start">
            <div className="col-span-12 lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.28em] font-semibold border border-[#ff0055]/40 text-[#ff0055] bg-[#ff0055]/10">
                <Star size={11} /> Beginner-friendly · Done-for-you
              </div>
              <div className="font-display font-black text-[28px] md:text-[40px] leading-[1.05] tracking-tight mt-5 max-w-xl">
                Full Production{" "}
                <span className="text-[#00f0ff]">by the hour.</span>
              </div>
              <div className="mt-5 text-[#a395c3] text-sm md:text-base leading-relaxed max-w-2xl">
                You bring the spark — we run the entire shoot. Built for
                first-time creators who want studio-grade results without
                learning a single piece of gear. Every booking opens with a
                <span className="text-white font-semibold"> 30-minute consultation</span>{" "}
                to lock in your goals, audience, and story — then our team
                drives the room: lighting, sound, cameras, direction, and
                on-set coaching. You just create.
              </div>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5 text-[13px] text-white/85 max-w-2xl">
                {[
                  "30-min strategy consult before every session",
                  "Full crew — we direct, light & roll",
                  "Zero production knowledge required",
                  "Multi-camera 4K coverage on every angle",
                  "Topic prompts & talking points if you need them",
                  "On-camera coaching & confidence prep",
                  "High-quality 4K imagery · 48 hr delivery via private servers",
                  "Post-production editing sold separately (à la carte)",
                  "Custom backdrop & lighting tuned to your brand",
                  "Hair & makeup station + private wardrobe room on-site",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2.5">
                    <Check
                      size={14}
                      className="text-[#00f0ff] mt-1 flex-shrink-0"
                      strokeWidth={2.5}
                    />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-10 md:mt-12 flex justify-center">
                <button
                  type="button"
                  onClick={() =>
                    window.dispatchEvent(
                      new CustomEvent("sb:open-booking", {
                        detail: { package: "brand_shoot_day" },
                      }),
                    )
                  }
                  data-testid="packages-full-production-cta"
                  className="inline-flex items-center gap-3 pl-7 pr-3 py-4 md:py-5 rounded-full bg-[#ff0055] text-white font-semibold text-base md:text-lg lg:text-xl hover:bg-white hover:text-[#05050a] transition-colors cursor-pointer touch-manipulation shadow-[0_8px_40px_-12px_rgba(255,0,85,0.55)]"
                >
                  Start with a consult
                  <span className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-black/30 flex items-center justify-center text-sm">
                    →
                  </span>
                </button>
              </div>
            </div>
            <div className="col-span-12 lg:col-span-5 grid grid-cols-2 gap-4">
              <div className="rounded-2xl p-5 bg-white/[0.03] border border-white/10">
                <div className="font-display text-3xl font-bold">from $360</div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#a395c3] mt-1.5">
                  per hour
                </div>
              </div>
              <div className="rounded-2xl p-5 bg-white/[0.03] border border-white/10">
                <div className="font-display text-3xl font-bold text-[#00f0ff]">
                  2 hr
                </div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#a395c3] mt-1.5">
                  minimum booking
                </div>
              </div>
              <div className="rounded-2xl p-5 bg-white/[0.03] border border-white/10 col-span-2">
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#ff0055] font-semibold">
                  Includes
                </div>
                <div className="text-sm text-white/85 mt-2 leading-snug">
                  Studio + full crew + 4K cameras + studio lighting +
                  professional audio + on-set direction.
                </div>
              </div>

              {/* Cinematic Sora-2 reel: guest gearing up + on-air cuts */}
              <StudioReelVideo />
            </div>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex justify-center mb-10"
          data-testid="packages-audience-toggle"
        >
          <div className="relative">
            {/* Static brand-palette glow — cyan on the Creators side, magenta on the Brands side */}
            <div
              aria-hidden="true"
              className="absolute -inset-1.5 rounded-full pointer-events-none"
              style={{
                background:
                  "linear-gradient(90deg, #00f0ff 0%, #6b21d9 50%, #ff0055 100%)",
                filter: "blur(12px)",
                opacity: 0.55,
              }}
            />
            <div
              aria-hidden="true"
              className="absolute -inset-0.5 rounded-full pointer-events-none"
              style={{
                background:
                  "linear-gradient(90deg, #00f0ff 0%, #6b21d9 50%, #ff0055 100%)",
                filter: "blur(4px)",
                opacity: 0.45,
              }}
            />
            <div className="relative inline-flex items-center gap-1.5 p-1.5 rounded-full bg-[#0a0612] border border-white/10 backdrop-blur-md">
              {AUDIENCES.map((a) => {
                const active = audience === a.id;
                return (
                  <button
                    key={a.id}
                    onClick={() => (isMobile ? jumpToAudience(a.id) : switchAudience(a.id))}
                    data-testid={`packages-tab-${a.id}`}
                    className={`relative inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full text-[13px] font-semibold transition-colors ${
                      active ? "text-[#05050a]" : "text-white/70 hover:text-white"
                    }`}
                  >
                    {active && (
                      <motion.span
                        layoutId="audience-pill"
                        className={`absolute inset-0 rounded-full ${
                          a.id === "brands" ? "bg-[#ff0055]" : "bg-[#00f0ff]"
                        }`}
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      />
                    )}
                    <a.icon size={14} className="relative" />
                    <span className="relative whitespace-nowrap">{a.label}</span>
                    <span
                      className={`hidden md:inline relative text-[10px] uppercase tracking-[0.2em] ${
                        active ? "text-[#05050a]/70" : "text-white/40"
                      }`}
                    >
                      · {a.sub}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Audience header line */}
        <motion.div
          key={current.id + "-sub"}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-center mb-10 max-w-2xl mx-auto"
        >
          <div className="font-display text-xl md:text-2xl font-semibold">
            <span className="text-[#00f0ff]">{current.headline}</span>{" "}
          </div>
          <div className="text-[#a395c3] text-sm md:text-base mt-2">
            {current.subhead}
          </div>
        </motion.div>

        {/* Tier cards — mobile: single flat carousel with prev/next.
            Desktop: 3-column grid inside a 3-panel audience-swipe wrapper. */}
        <div
          ref={carouselRef}
          className="overflow-x-clip py-6 relative"
          data-testid="packages-carousel"
        >
          {isMobile ? (
            // ── MOBILE — single infinite-loop carousel across ALL audiences ──
            <div className="relative">
              <div className="overflow-hidden" ref={emblaRef} data-testid={`packages-grid-${current.id}`}>
                <div className="flex gap-4 -ml-4 touch-pan-y">
                  {flatTiers.map((t, i) => (
                    <PackageCardMobile key={`${t.audienceId}-${t.name}-${i}`} tier={t} index={i} />
                  ))}
                </div>
              </div>

              {/* Prev / Next controls + audience-aware dots */}
              <div className="mt-4 flex items-center justify-between gap-4 px-1">
                <button
                  type="button"
                  aria-label="Previous package"
                  onClick={emblaPrev}
                  data-testid="packages-prev"
                  className="w-10 h-10 rounded-full border border-white/15 bg-white/[0.03] text-white/80 flex items-center justify-center hover:border-white/30 hover:text-white active:scale-95 transition-all"
                >
                  <ArrowRight size={16} className="rotate-180" />
                </button>
                <div className="flex items-center gap-2" data-testid="packages-tier-dots">
                  {flatTiers.map((t, i) => (
                    <button
                      key={`dot-${i}`}
                      type="button"
                      aria-label={`Go to ${t.name}`}
                      onClick={() => emblaApi && emblaApi.scrollTo(i)}
                      className={`h-1.5 rounded-full transition-all ${
                        flatIndex === i
                          ? t.audienceId === "brands" ? "w-6 bg-[#ff0055]" : "w-6 bg-[#00f0ff]"
                          : "w-1.5 bg-white/25"
                      }`}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  aria-label="Next package"
                  onClick={emblaNext}
                  data-testid="packages-next"
                  className={`w-10 h-10 rounded-full border flex items-center justify-center active:scale-95 transition-all ${
                    audience === "brands"
                      ? "border-[#ff0055]/50 bg-[#ff0055]/10 text-[#ff0055] hover:bg-[#ff0055]/20"
                      : "border-[#00f0ff]/40 bg-[#00f0ff]/10 text-[#00f0ff] hover:bg-[#00f0ff]/20"
                  }`}
                >
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>
          ) : (
            // ── DESKTOP — original 3-panel audience-swipe carousel ─────────
          <motion.div
            className="flex"
            style={{ width: "300%" }}
            animate={{ x: `${(-slideIndex * 100) / 3}%` }}
            transition={
              enableAnim
                ? { type: "spring", stiffness: 110, damping: 26, mass: 1.3 }
                : { duration: 0 }
            }
            onAnimationComplete={onSlideComplete}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.22}
            dragTransition={{ bounceStiffness: 320, bounceDamping: 26 }}
            onDragEnd={(_, info) => handleSwipe(info.offset.x, info.velocity.x)}
          >
            {panelOrder.map((aud, panelI) => (
              <div
                key={`${aud.id}-slot-${panelI}`}
                style={{ width: `${100 / 3}%` }}
                className="shrink-0 select-none touch-pan-y group/cards"
                data-testid={
                  panelI === 1 ? `packages-grid-${aud.id}` : undefined
                }
                aria-hidden={panelI !== 1}
              >
                <div className="grid grid-cols-3 gap-5">
                {aud.tiers.map((t, i) => (
                  <PackageCardDesktop
                    key={`${panelI}-${aud.id}-${t.name}`}
                    tier={t}
                    audienceId={aud.id}
                    index={i}
                  />
                ))}
                </div>
              </div>
            ))}
          </motion.div>
          )}
        </div>

        {/* Mobile dots + prev/next now live inside the mobile branch above. */}

        {/* Add-ons (creators only) */}
        {current.showAddOns && (
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            className="mt-14"
            key="add-ons"
          >
            <motion.div variants={reveal} custom={0} className="flex items-center gap-3 mb-6">
              <Plus size={14} className="text-[#ff0055]" />
              <span className="text-[11px] uppercase tracking-[0.32em] text-[#a395c3]">
                Creator add-ons (à-la-carte)
              </span>
              <span className="flex-1 h-px bg-white/10" />
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {ADD_ONS.map((a, i) => (
                <motion.button
                  type="button"
                  key={a.label}
                  variants={reveal}
                  custom={i}
                  onClick={() => setAlaCarteOpen(true)}
                  data-testid={`addon-${i}`}
                  className="text-left rounded-2xl border border-white/10 bg-white/[0.025] p-5 hover:border-[#ff0055]/40 hover:bg-white/[0.05] hover:scale-[1.015] active:scale-[0.99] transition-all cursor-pointer touch-manipulation"
                >
                  <div className="font-display text-base font-semibold">
                    {a.label}
                  </div>
                  <div className="font-mono-tight text-sm text-[#00f0ff] mt-2">
                    {a.price}
                  </div>
                  <div className="mt-3 text-[10px] uppercase tracking-[0.24em] text-[#a395c3]/70">
                    Tap for details →
                  </div>
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Quiz + Discovery Call CTA block (merged) */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mt-14 relative rounded-3xl p-8 md:p-12 glow-border bg-[#11091a]/70 backdrop-blur-md"
          data-testid="package-quiz-cta"
        >
          {/* Pulsating cyan ↔ magenta halo behind the block */}
          <motion.div
            className="absolute -inset-16 pointer-events-none -z-10"
            aria-hidden="true"
            style={{ filter: "blur(24px)" }}
            animate={{
              background: [
                "radial-gradient(ellipse 60% 80% at 50% 50%, rgba(0,240,255,0.22) 0%, rgba(0,240,255,0.10) 45%, transparent 75%)",
                "radial-gradient(ellipse 60% 80% at 50% 50%, rgba(255,0,85,0.22) 0%, rgba(255,0,85,0.10) 45%, transparent 75%)",
                "radial-gradient(ellipse 60% 80% at 50% 50%, rgba(0,240,255,0.22) 0%, rgba(0,240,255,0.10) 45%, transparent 75%)",
              ],
            }}
            transition={{ duration: 7, ease: "easeInOut", repeat: Infinity }}
          />
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#00f0ff]/20 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-[#ff0055]/15 blur-3xl pointer-events-none" />

          <div className="relative grid grid-cols-12 gap-6 items-center">
            <div className="col-span-12 lg:col-span-7">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-[#00f0ff]">
                <Compass size={12} /> Not sure where to start?
              </div>
              <h3 className="font-display font-black text-3xl md:text-[40px] lg:text-[48px] tracking-tighter leading-[1.05] mt-4">
                Not sure which package is right for you?{" "}
                <span className="italic font-light text-[#a395c3]">
                  Let&apos;s figure it out.
                </span>
              </h3>
              <p className="text-[#a395c3] text-sm md:text-base mt-4 max-w-xl">
                Answer 5 quick questions and we&apos;ll recommend the package
                that fits your goals, your output, and your budget. Takes under
                a minute.
              </p>
            </div>
            <div className="col-span-12 lg:col-span-5 flex flex-col items-center gap-5">
              <button
                onClick={() => setQuizOpen(true)}
                data-testid="open-package-quiz"
                className="group inline-flex items-center justify-center gap-4 pl-8 pr-4 py-5 md:py-6 rounded-full bg-[#00f0ff] text-[#05050a] font-semibold text-lg md:text-xl lg:text-2xl hover:bg-white transition-colors shadow-[0_8px_40px_-12px_rgba(0,240,255,0.55)]"
              >
                Take the quiz
                <span className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#05050a] text-[#00f0ff] flex items-center justify-center group-hover:rotate-[-45deg] transition-transform">
                  <ArrowRight size={20} />
                </span>
              </button>

              <div
                className="flex items-center gap-3 w-full max-w-xs"
                aria-hidden="true"
              >
                <span className="flex-1 h-px bg-white/10" />
                <span className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3]/70">
                  or
                </span>
                <span className="flex-1 h-px bg-white/10" />
              </div>

              <div
                className="text-[#a395c3] text-sm md:text-[15px] text-center max-w-xs"
                data-testid="discovery-call-prompt"
              >
                Still not sure which lane to pick?
              </div>

              <a
                href="#book"
                onClick={(e) => {
                  e.preventDefault();
                  window.dispatchEvent(
                    new CustomEvent("sb:open-discovery"),
                  );
                }}
                data-testid="brand-discovery-cta-btn"
                className="group inline-flex items-center justify-center gap-3 pl-7 pr-3 py-4 md:py-5 rounded-full bg-white text-[#05050a] font-semibold text-base md:text-lg hover:bg-[#ff0055] hover:text-white transition-colors shadow-[0_8px_30px_-12px_rgba(0,240,255,0.35)]"
              >
                Book a discovery call
                <span className="w-10 h-10 rounded-full bg-[#05050a]/10 group-hover:bg-black/40 flex items-center justify-center transition-colors">
                  <ArrowRight size={16} className="group-hover:rotate-[-45deg] transition-transform" />
                </span>
              </a>

              <a
                href="https://calendar.app.google/kqqnFx17fWZX7msW8"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="discovery-google-external-link"
                className="text-[11px] uppercase tracking-[0.24em] text-[#a395c3]/70 hover:text-[#00f0ff] transition-colors inline-flex items-center gap-2"
              >
                Prefer Google Calendar? Pick a slot →
              </a>
            </div>
          </div>
        </motion.div>
      </div>

      <PackageQuiz open={quizOpen} onClose={() => setQuizOpen(false)} />
      <AlaCarteModal open={alaCarteOpen} onClose={() => setAlaCarteOpen(false)} />
    </section>
  );
};

export default Packages;
